import React, { type FC, useMemo } from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import {
  Announcement01,
  Award03,
  Bank,
  BankNote02,
  BarChartSquare01,
  Bell01,
  BookClosed,
  BookmarkCheck,
  BookOpen01,
  BookOpen02,
  Building02,
  Building05,
  Building07,
  Calculator,
  CheckVerified03,
  Code02,
  CoinsHand,
  CreditCard02,
  Data,
  Dataflow02,
  Dataflow03,
  Dotpoints01,
  File06,
  FileCheck02,
  Fingerprint02,
  Globe01,
  GoogleChrome,
  GraduationHat01,
  Grid01,
  HelpCircle,
  HomeLine,
  ImageCheck,
  Inbox01,
  List,
  LogOut01,
  MessageChatSquare,
  MessageTextSquare01,
  Monitor01,
  Monitor02,
  Phone02,
  PhoneCall01,
  PhoneCall02,
  PlusCircle,
  Rocket02,
  Rows01,
  SearchSm,
  Settings01,
  ShieldTick,
  ShoppingBag01,
  ShoppingCart03,
  Stand,
  Tablet02,
  Tag03,
  TerminalSquare,
  Tool02,
  TrendUp01,
  Tv01,
  User01,
  Users01,
  UsersEdit,
  UserSquare,
  Wallet01,
} from '@flipdish/ui-icons';
import type {
  NavAccountData,
  NavAccountMenuItemType,
  SidebarNavigationProps,
} from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { type TranslateFunction, getActiveLanguage, getTranslate } from 'react-localize-redux';
import { add, remove } from 'react-redux-permissions';
import { useLocation } from 'react-router-dom';

import { accountActions } from '../../actions/account.actions';
import { useHasAccessToOnboardingV3 } from '../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from '../../components/RMS/utils/permissions';
import { displayName } from '../../helpers/account';
import { accountRoutesConst as routes } from '../../routes/account.routes';
import { isProductBasedMenusEnabled } from '../../selectors/app.selector';
import {
  type PortalUser,
  type PortalViewId,
  selectPortalUser,
  shouldDisplay,
} from '../../viewConfiguration';
import { type ItemMetaData, menuItemsConfig } from './menuItemsConfig';

type SidebarNavItem = SidebarNavigationProps['items'][number];
type SidebarSubItem = NonNullable<SidebarNavItem['items']>[number];
type NavIcon = FC<{ className?: string }>;

/**
 * Wraps a rendered JSX element into an FC<{className?: string}> that
 * SidebarNavigation can mount with its own className for sizing/colour.
 */
const wrapIcon = (element: React.ReactElement): NavIcon => {
  const WrappedIcon: NavIcon = ({ className }) => React.cloneElement(element, { className });
  return WrappedIcon;
};

export const hasAccess = (
  portalUser: PortalUser,
  itemPermissions?: AppState['permissions'],
  viewId?: PortalViewId,
  exemptFromViewConfiguration?: boolean
): boolean => {
  return shouldDisplay(portalUser, viewId, {
    legacyVisibilityPermissions: itemPermissions,
    exemptFromViewConfiguration,
  });
};

// Icons from the "Sidebar navigation" design (Notion FDS seq 11); titles it omits are marked "not in design".
// TODO: once the legacy sidebar is removed, set these on menuItemsConfig entries and delete both maps and wrapIcon.
const topLevelIconOverrides: Record<string, NavIcon> = {
  Home: HomeLine,
  Sales: ShoppingBag01,
  Orders: ShoppingBag01,
  Properties: Building02,
  Stores: Building02,
  Menu_Editor: BookOpen01,
  Menus: BookOpen01,
  Catalog: BookOpen01,
  Reports: BarChartSquare01,
  Marketing: Announcement01,
  Finance: BankNote02,
  Sales_channels: Monitor02,
  // Ticket says bank-note-02 (Finance's icon); the Figma frame shows a plus circle.
  Add_ons: PlusCircle,
  First_Time_Setup: Rocket02, // not in design
  Support: HelpCircle,
  developer_tools: TerminalSquare,
  Settings: Settings01,
  Flipdish_Staff_Tools: Tool02,
};

const subItemIconOverrides: Record<string, NavIcon> = {
  // Marketing
  Customers: UserSquare,
  Vouchers: Tag03,
  Campaigns: Announcement01,
  Push_Notifications: Bell01,
  Flipdish_Phone_Marketing: PhoneCall01,
  Flipdish_Phone_Marketing_beta: PhoneCall02,
  AI_Marketing: TrendUp01,
  SMS_campaigns: MessageTextSquare01, // not in design
  Google_Listing_Management: GoogleChrome,

  // Finance
  Payouts: CoinsHand,
  Bank_accounts: Bank,
  Verifactu: ShieldTick,
  Accounts: Calculator,
  Payroll: Wallet01,
  Restaurant_vouchers: CreditCard02, // not in design
  Ledger: BookClosed, // not in design

  // Sales channels
  Point_of_Sale: Tv01,
  Mobile_apps: Phone02,
  Website: Globe01,
  Kiosks: Monitor01,
  Marketplaces: ShoppingCart03,
  Phone_agent: Phone02, // not in design

  // Catalog
  Products: BookOpen01,
  Modifier_Groups: BookOpen01,

  // Add ons
  App_Store: Grid01,
  FoodSafety: Award03,
  Inventory: Dotpoints01,
  Signage: Stand,
  Terminals: Tablet02,
  Documents: File06, // not in design
  Central_kitchen: Building05, // not in design

  // Support
  Support_articles: BookOpen02,
  Talk_to_us: MessageChatSquare,
  Flipdish_Academy: GraduationHat01,

  // Developer tools
  Webhooks: Dataflow02,
  Oauth_apps_link: Fingerprint02,
  App_store_apps: Data,
  Metafields: Code02,

  // Settings
  Organization: Building07,
  Brand: BookmarkCheck,
  Users: Users01,
  Teammates: Users01,
  Staff: UsersEdit, // not in design under Settings; Users already takes users-01
  Entitlements: CheckVerified03,
  Entitlements_V2: CheckVerified03,
  Sales_channels: Monitor02,
  Sales_channel_groups: Rows01,
  Customer_messages: Inbox01,
  Bulk_Updater: Dataflow02,
  Audit_logs: List,

  // Flipdish staff tools
  Bulk_Settings_Updater: Dataflow02,
  Advanced_Restaurant_Search: SearchSm,
  Advanced_User_Search: UsersEdit,
  Org_View: Building07,
  System_settings_for_all_orgs: Settings01,
  Logo_Resizer: ImageCheck,
  Onboarding_Configuration_Tool: Rocket02,
  Mobile_Apps_Tools: Phone02,
  Gloria_Food: Dataflow03,
};

type TransformOptions = {
  /** Called with each visible `isMFE` href; library items carry only `href`, so the host tracks MFE targets itself. */
  onMfeHref?: (href: string) => void;
};

export const transformSubItems = (
  subItems: NonNullable<ItemMetaData['subItems']>,
  portalUser: PortalUser,
  translate: TranslateFunction,
  search: string,
  options: TransformOptions = {}
): SidebarSubItem[] => {
  return subItems
    .filter((item) => !item.hidden)
    .filter((item) =>
      hasAccess(portalUser, item.permissions, item.viewId, item.exemptFromViewConfiguration)
    )
    .map((item) => {
      const href = item.keepQuery ? `${item.path}${search}` : item.path;
      if (item.isMFE) options.onMfeHref?.(href);
      return {
        label: translate(item.title) as string,
        href,
        ...(item.icon ? { icon: subItemIconOverrides[item.title] ?? wrapIcon(item.icon) } : {}),
        ...(item.isBeta ? { badge: translate('Beta') as string } : {}),
      };
    });
};

type ItemToggle = NonNullable<SidebarNavItem['toggle']>;

export const transformItems = (
  rawItems: ItemMetaData[],
  portalUser: PortalUser,
  translate: TranslateFunction,
  search: string,
  getToggle?: (item: ItemMetaData) => ItemToggle | undefined,
  options: TransformOptions = {}
): SidebarNavItem[] => {
  return rawItems
    .filter((item) => !item.hidden)
    .filter((item) =>
      hasAccess(portalUser, item.permissions, item.viewId, item.exemptFromViewConfiguration)
    )
    .map((item): SidebarNavItem | null => {
      const nested = item.subItems || item.subDrawerItems;
      const subItems = nested
        ? transformSubItems(nested, portalUser, translate, search, options)
        : undefined;

      // Parent-only items (no href) with no visible children should be dropped
      if (!item.path && (!subItems || subItems.length === 0)) {
        return null;
      }

      if (item.path && item.isMFE) options.onMfeHref?.(item.path);

      const toggle = getToggle?.(item);

      return {
        label: translate(item.title) as string,
        ...(item.path ? { href: item.path } : {}),
        icon: topLevelIconOverrides[item.title] ?? wrapIcon(item.icon),
        ...(subItems?.length ? { items: subItems } : {}),
        ...(item.isBeta ? { badge: translate('Beta') as string } : {}),
        ...(toggle ? { toggle } : {}),
      };
    })
    .filter((item): item is SidebarNavItem => item !== null);
};

/** Footer groups, in display order. Support is synthesised below. */
const FOOTER_TITLES: Array<ItemMetaData['title']> = [
  'developer_tools',
  'Settings',
  'Flipdish_Staff_Tools',
];

export const useSidebarNavItems = () => {
  const dispatch = useAppDispatch();
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const currentApp = useAppSelector((state) => state.currentApp);
  const account = useAppSelector((state) => state.account);
  const portalUser = useAppSelector(selectPortalUser);
  const isProductBasedMenusOn = useAppSelector((state) => isProductBasedMenusEnabled(state));
  const { data: rmsData } = useQueryRMSCommonDataHook(currentApp?.AppId || '');
  const subscriptions = rmsData?.subscriptions;
  const menuSource = rmsData?.associations?.menuSource;
  const location = useLocation();

  const handleHiddenFeatureChange = (isSelected: boolean) => {
    void dispatch(accountActions.update({ ...account, ShowHiddenFeatures: isSelected }))
      .then(() => {
        dispatch(isSelected ? add('ShowHiddenFeatures') : remove('ShowHiddenFeatures'));
      })
      .catch(() => {
        // accountActions.update already dispatches updateFailure.
      });
  };

  const activeLanguage = useAppSelector((state) => getActiveLanguage(state.locale));
  const appId = currentApp?.AppId ?? 'global';
  const { reducedOnboardingView, onboardingWaitingView } = useHasAccessToOnboardingV3();
  const isRestrictedOnboardingNav = reducedOnboardingView || onboardingWaitingView;
  const showAppScopedLinks =
    !isRestrictedOnboardingNav && appId !== 'global' && appId !== 'flipdish-global';

  // menuItemsConfig uses hooks internally so it must be called at the top level
  const rawItems = menuItemsConfig(currentApp, isProductBasedMenusOn, menuSource, subscriptions);

  const footerRaw = FOOTER_TITLES.flatMap((title) =>
    rawItems.filter((item) => item.title === title)
  );
  const mainRaw = rawItems.filter((item) => !FOOTER_TITLES.includes(item.title));

  const getStaffToolsToggle = (item: ItemMetaData): ItemToggle | undefined => {
    if (item.title !== 'Flipdish_Staff_Tools') return undefined;
    return {
      label: translate('Show_hidden_features') as string,
      isSelected: !!account.ShowHiddenFeatures,
      onChange: handleHiddenFeatureChange,
    };
  };

  const mfeHrefs = new Set<string>();
  const collectMfe: TransformOptions = { onMfeHref: (href) => mfeHrefs.add(href) };

  const items = transformItems(
    mainRaw,
    portalUser,
    translate,
    location.search,
    undefined,
    collectMfe
  );
  const configFooterItems = transformItems(
    footerRaw,
    portalUser,
    translate,
    location.search,
    getStaffToolsToggle,
    collectMfe
  );

  const supportedLanguages = ['en', 'fr', 'de', 'es', 'nl'];
  const langCode = supportedLanguages.includes(activeLanguage) ? activeLanguage : 'en';

  const supportItem: SidebarNavItem = {
    label: translate('Support') as string,
    icon: topLevelIconOverrides.Support,
    items: [
      {
        label: translate('Support_articles') as string,
        href: `https://help.flipdish.com/${langCode}`,
        icon: subItemIconOverrides.Support_articles,
      },
      {
        label: translate('Talk_to_us') as string,
        href: 'https://help.flipdish.com/s/contactsupport?language=en_GB',
        icon: subItemIconOverrides.Talk_to_us,
      },
      {
        label: translate('Flipdish_Academy') as string,
        href: 'https://academy.flipdish.com/',
        icon: subItemIconOverrides.Flipdish_Academy,
      },
    ],
  };

  const footerItems: SidebarNavItem[] = [
    ...(!isRestrictedOnboardingNav ? [supportItem] : []),
    ...configFooterItems,
  ];

  const subscriptionPermissions: AppState['permissions'] = [
    'Owner',
    ...propertyManagerRolePermission,
    ...propertyOwnerRolePermission,
    'ManagedOwner',
    'FinanceManger',
    'FlipdishStaff',
  ];
  const hasSubscriptionAccess =
    showAppScopedLinks && hasAccess(portalUser, subscriptionPermissions);

  // Developer tools moved to the footer nav (menuItemsConfig).
  const accountData: NavAccountData = useMemo(() => {
    const menuItems: NavAccountMenuItemType[] = [
      {
        label: translate('My_account') as string,
        icon: User01,
        href: `/${appId}/profile`,
      },
    ];

    if (hasSubscriptionAccess) {
      menuItems.push(
        {
          label: translate('Subscriptions') as string,
          icon: CreditCard02,
          href: `/${appId}/billing/org-subscriptions/subscriptions`,
        },
        {
          label: translate('Invoices') as string,
          icon: FileCheck02,
          href: `/${appId}/finance/invoices`,
        }
      );
    }

    if (!isRestrictedOnboardingNav) {
      menuItems.push({
        label: translate('Communications') as string,
        icon: Bell01,
        href: `/${appId}/communication-preferences/user`,
      });
    }

    return {
      account: {
        id: String(account?.AccountId ?? ''),
        name: displayName(account),
        email: account?.Email || '',
        avatar: '',
        status: 'online' as const,
      },
      menuItems,
      bottomMenuItems: [
        {
          label: translate('Logout') as string,
          icon: LogOut01,
          href: routes.Logout,
        },
      ],
    };
  }, [account, translate, appId, hasSubscriptionAccess, isRestrictedOnboardingNav]);

  return {
    items,
    footerItems,
    account: accountData,
    activeUrl: location.pathname,
    // MFEs only re-read the URL on popstate, which history.push never fires. Wait a frame so the
    // push has landed. Same trick as the legacy sidebar's MenuItem.tsx.
    onNavigate: (href: string) => {
      if (!mfeHrefs.has(href)) return;
      requestAnimationFrame(() => window.dispatchEvent(new PopStateEvent('popstate')));
    },
  };
};
